/**
 * MKS-164 — task labels API client. Internal-only surface (every backend
 * route uses `authenticate`, never `authenticateAny`).
 */
import { apiGet, apiPost, apiPatch, apiDelete, apiPut } from './client';

export interface TaskLabelRecord {
  id: number;
  name: string;
  color: string;
  projectId: number | null;
}

/**
 * GET /api/projects/:projectId/task-labels
 * Gate: authenticate + requireAdminOrProjectMemberByProject (admin / PM /
 * project member). Returns the global (projectId null) UNION the project's
 * own labels, ordered projectId then name.
 */
export async function listProjectTaskLabels(projectId: number): Promise<TaskLabelRecord[]> {
  const res = await apiGet<{ data: TaskLabelRecord[] }>(`/projects/${projectId}/task-labels`);
  return res.data;
}

/**
 * POST /api/projects/:projectId/task-labels
 * Gate: authenticate + requireAdminOrProjectPM (admin or the project's PM).
 */
export async function createProjectTaskLabel(
  projectId: number,
  input: { name: string; color: string },
): Promise<TaskLabelRecord> {
  const res = await apiPost<{ data: TaskLabelRecord }>(
    `/projects/${projectId}/task-labels`,
    input,
  );
  return res.data;
}

/**
 * PATCH /api/task-labels/:id
 * Gate: authenticate only at the route — service-layer
 * assertCanMutateLabel resolves the label's own projectId (admin-or-owning-PM
 * for a project-scoped label; admin-only for a GLOBAL label).
 */
export async function updateTaskLabel(
  labelId: number,
  patch: { name?: string; color?: string },
): Promise<TaskLabelRecord> {
  const res = await apiPatch<{ data: TaskLabelRecord }>(`/task-labels/${labelId}`, patch);
  return res.data;
}

/**
 * DELETE /api/task-labels/:id
 * Gate: same service-layer check as updateTaskLabel.
 */
export async function deleteTaskLabel(labelId: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/task-labels/${labelId}`);
}

/**
 * PUT /api/tasks/:taskId/labels
 * Gate: authenticate + requireAdminOrProjectMemberByTask (admin / PM /
 * project member of the TASK's project). Whole-set replace: sends the FULL
 * new labelIds array, not a delta.
 */
export async function setTaskLabels(
  taskId: number,
  labelIds: number[],
): Promise<TaskLabelRecord[]> {
  const res = await apiPut<{ data: TaskLabelRecord[] }>(`/tasks/${taskId}/labels`, { labelIds });
  return res.data;
}

// ---------------------------------------------------------------------------
// MKS-406 — project-scoped labels.
// ---------------------------------------------------------------------------

export interface SharedTaskLabelRecord {
  id: number;
  name: string;
  color: string;
  /** false = the project has switched this global label off. */
  enabled: boolean;
}

/**
 * GET /api/projects/:projectId/shared-task-labels
 * Gate: authenticate + requireAdminOrProjectPM.
 * The general global labels the project may switch off (robot/SRE labels excluded).
 */
export async function listProjectSharedTaskLabels(
  projectId: number,
): Promise<SharedTaskLabelRecord[]> {
  const res = await apiGet<{ data: SharedTaskLabelRecord[] }>(
    `/projects/${projectId}/shared-task-labels`,
  );
  return res.data;
}

/**
 * PUT /api/projects/:projectId/shared-task-labels
 * Gate: same as the GET. Whole-set replace of the switched-off global label ids.
 */
export async function setProjectDisabledTaskLabels(
  projectId: number,
  disabledLabelIds: number[],
): Promise<SharedTaskLabelRecord[]> {
  const res = await apiPut<{ data: SharedTaskLabelRecord[] }>(
    `/projects/${projectId}/shared-task-labels`,
    { disabledLabelIds },
  );
  return res.data;
}

export interface GlobalTaskLabelRecord extends TaskLabelRecord {
  /** Used by the Robot/SRE by name: rename and delete are refused (color is editable). */
  system: boolean;
}

/**
 * GET /api/task-labels
 * Gate: authenticate + requireAdmin.
 */
export async function listGlobalTaskLabels(): Promise<GlobalTaskLabelRecord[]> {
  const res = await apiGet<{ data: GlobalTaskLabelRecord[] }>('/task-labels');
  return res.data;
}

/**
 * POST /api/task-labels
 * Gate: same as listGlobalTaskLabels. 409 on a duplicate global name.
 */
export async function createGlobalTaskLabel(input: {
  name: string;
  color: string;
}): Promise<GlobalTaskLabelRecord> {
  const res = await apiPost<{ data: GlobalTaskLabelRecord }>('/task-labels', input);
  return res.data;
}
